import { useMemo } from 'react';

import { themeObjectToString } from '@atlaskit/tokens/theme-object-to-string';
import { themeStringToObject } from '@atlaskit/tokens/theme-string-to-object';

import { useThemingService } from '@confluence/whiteboard-common/entry-points/useThemingService.ts';
import { useIsMobileApp } from '@confluence/mobile-app-detection/entry-points/MobileAppDetection.ts';

import {
	BRIDGE_CLIENT_QUERY_PARAM,
	EMBEDDED_THEME_STATE,
	NATIVE_EMBED_FULLSCREEN_QUERY_PARAM,
	PRESENTATION_MODE_QUERY_PARAM,
	PRESENTATION_MODE_MOBILE_APP,
	PARENT_PRODUCT_QUERY_PARAM,
	PARENT_PRODUCT_SMART_LINK,
	PARENT_PRODUCT_ROVO,
} from '../database-params.ts';

export const useInitialUrl = ({
	version,
	isSmartLinkEmbed,
	versionId,
	presentationMode,
	isNativeEmbedFullscreen,
	bridgeClient,
	isRovoParentProduct,
}: {
	version?: string;
	isSmartLinkEmbed?: boolean;
	versionId?: string | null;
	presentationMode?: string;
	isNativeEmbedFullscreen?: boolean;
	bridgeClient?: string;
	isRovoParentProduct?: boolean;
}): string => {
	const { initialThemeSerialized } = useThemingService();
	const { isMobileApp } = useIsMobileApp();

	return useMemo(() => {
		const url = new URL(`/databases/standalone`, window.location.origin);

		if (version) {
			url.searchParams.set('bifrost-override', version);
		}

		if (isSmartLinkEmbed) {
			url.searchParams.set(PARENT_PRODUCT_QUERY_PARAM, PARENT_PRODUCT_SMART_LINK);
		}

		if (isMobileApp) {
			url.searchParams.set(PRESENTATION_MODE_QUERY_PARAM, PRESENTATION_MODE_MOBILE_APP);
		}

		if (presentationMode !== undefined) {
			url.searchParams.set(PRESENTATION_MODE_QUERY_PARAM, presentationMode);
		}

		if (isNativeEmbedFullscreen) {
			url.searchParams.set(NATIVE_EMBED_FULLSCREEN_QUERY_PARAM, 'true');
		}

		if (isRovoParentProduct) {
			url.searchParams.set(PARENT_PRODUCT_QUERY_PARAM, PARENT_PRODUCT_ROVO);
		}

		if (initialThemeSerialized) {
			const themeObject = themeStringToObject(initialThemeSerialized);
			url.pathname = `${url.pathname}/${themeObject.colorMode === 'dark' ? 'index-dark.html' : 'index.html'}`;
			url.searchParams.set(
				EMBEDDED_THEME_STATE,
				encodeURIComponent(themeObjectToString(themeObject)),
			);
		}

		if (versionId) {
			url.searchParams.set('version', versionId);
		}

		if (bridgeClient) {
			url.searchParams.set(BRIDGE_CLIENT_QUERY_PARAM, bridgeClient);
		}

		const finalUrlParts = [url.pathname];
		if (Array.from(url.searchParams).length > 0) {
			finalUrlParts.push(url.searchParams.toString());
		}

		return finalUrlParts.join('?');
	}, [
		version,
		isSmartLinkEmbed,
		isMobileApp,
		initialThemeSerialized,
		versionId,
		presentationMode,
		isNativeEmbedFullscreen,
		bridgeClient,
		isRovoParentProduct,
	]);
};
